/**
 * API client for Laravel backend.
 * Base URL: NEXT_PUBLIC_API_URL (e.g. http://localhost:8000).
 * Laravel should expose JSON API routes that return these shapes; if not yet available,
 * add routes in routes/api.php that mirror FrontendController data.
 */

import { cache } from 'react';
import { sanitizeClassifiedAdsPublic, sanitizeProductsPublic } from '@/lib/classifiedPublic';
import { responseBodyLooksLikeHtml } from '@/lib/maintenance';
import { getNextProxyBase } from '@/lib/nextProxy';
import { buildLaravelApiUrl } from '@/lib/site';

const getBaseUrl = () => process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8000';

/** Thrown by fetchApi when the API returns a non-2xx status. statusCode is set so callers can redirect on 404. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Laravel maintenance mode (503 JSON or HTML maintenance redirect). */
export class MaintenanceModeError extends Error {
  constructor(message = 'Site is under maintenance') {
    super(message);
    this.name = 'MaintenanceModeError';
  }
}

function logFetchApiFailure(url: string, status: number, text: string): void {
  if (process.env.NEXT_DEBUG_API === '1' || process.env.NODE_ENV !== 'production') {
    console.error('[fetchApi]', { url, status, preview: text.slice(0, 400) });
  }
}

function parseJsonBody<T>(url: string, status: number, text: string): T {
  if (responseBodyLooksLikeHtml(text)) {
    logFetchApiFailure(url, status, text);
    throw new MaintenanceModeError(
      `Request to ${url} returned HTML instead of JSON (often maintenance mode or WAF).`,
    );
  }
  if (!text.trim()) {
    throw new ApiError(`Empty response from ${url}`, status);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    logFetchApiFailure(url, status, text);
    throw new ApiError(
      `Invalid JSON from ${url}: ${text.slice(0, 240)}`,
      status,
    );
  }
}

export async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const url = path.startsWith('http') ? path : (() => {
    // Server-side: bypass Imperva WAF with direct internal URL
    if (typeof window === 'undefined') {
      const internal = (
        process.env.LARAVEL_API_URL ??
        process.env.NEXT_PUBLIC_API_URL ??
        'http://127.0.0.1:8001'
      ).replace(/\/$/, '');
      return `${internal}${path.startsWith('/') ? path : `/${path}`}`;
    }
    // Browser-side: use public URL via Nginx
    return buildLaravelApiUrl(path);
  })();
  const { headers: optionHeaders, ...rest } = options ?? {};
  const res = await fetch(url, {
    ...rest,
    redirect: 'manual',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...optionHeaders,
    },
  });

  const text = await res.text();

  if (res.status >= 300 && res.status < 400) {
    const location = res.headers.get('location') ?? '';
    logFetchApiFailure(url, res.status, text || location);
    if (/maintenance/i.test(location) || responseBodyLooksLikeHtml(text)) {
      throw new MaintenanceModeError(
        `API redirected (${res.status}) to maintenance: ${location || 'HTML body'}`,
      );
    }
    throw new ApiError(`API redirect ${res.status} to ${location}`, res.status);
  }

  if (!res.ok) {
    if (res.status === 503 && text.trim()) {
      try {
        const body = JSON.parse(text) as { maintenance_mode?: boolean; message?: string };
        if (body?.maintenance_mode) {
          throw new MaintenanceModeError(body.message ?? 'Site is under maintenance');
        }
      } catch (err) {
        if (err instanceof MaintenanceModeError) {
          throw err;
        }
      }
    }
    const isHtml = responseBodyLooksLikeHtml(text);
    let message = `API error ${res.status}: ${text || res.statusText}`;
    if (isHtml) {
      throw new MaintenanceModeError(
        `API error ${res.status}: ${url} returned HTML (maintenance mode or wrong API URL).`,
      );
    }
    logFetchApiFailure(url, res.status, text);
    throw new ApiError(message, res.status);
  }

  return parseJsonBody<T>(url, res.status, text);
}

/** Home page payload (SSR). Expect Laravel app mount + GET /home. */
export async function fetchHome() {
  const data = await fetchApi<HomePayload>('/home', { next: { revalidate: 120 } });
  return {
    ...data,
    products: sanitizeProductsPublic(data.products) as HomePayload['products'],
    promotedProducts: sanitizeProductsPublic(data.promotedProducts) as HomePayload['promotedProducts'],
    trendingProducts: sanitizeProductsPublic(data.trendingProducts) as HomePayload['trendingProducts'],
    trendingClassifiedAds: sanitizeClassifiedAdsPublic(data.trendingClassifiedAds) as HomePayload['trendingClassifiedAds'],
    classifiedAds: sanitizeClassifiedAdsPublic(data.classifiedAds) as HomePayload['classifiedAds'],
    boostedClassifiedAds: sanitizeClassifiedAdsPublic(data.boostedClassifiedAds) as HomePayload['boostedClassifiedAds'],
  };
}

/** Categories list (SSR). Expect Laravel app mount + GET /categories. */
export const fetchCategories = cache(async () => {
  // Includes branding settings (logo/favicon/site_name); keep fresh so admin updates appear quickly.
  return fetchApi<CategoriesPayload>('/categories', { next: { revalidate: 300 } });
});

/**
 * Location autocomplete suggestions for the category sidebar. Browser-fetch; no SSR caching
 * since this is called per-keystroke. Cancel with AbortSignal to avoid stale results overwriting newer ones.
 */
export async function fetchLocations(q: string, signal?: AbortSignal): Promise<{ id: number; name: string }[]> {
  const query = new URLSearchParams();
  const trimmed = q.trim();
  if (trimmed !== '') query.set('q', trimmed);
  query.set('limit', '10');
  const qs = query.toString();
  const path = `/locations${qs ? `?${qs}` : ''}`;
  try {
    const data = await fetchApi<{ data: { id: number; name: string }[] }>(path, {
      cache: 'no-store',
      signal,
    });
    return Array.isArray(data?.data) ? data.data : [];
  } catch (err) {
    // Treat aborted requests as empty results; don't pollute logs for the normal "user kept typing" flow.
    if (err instanceof Error && (err.name === 'AbortError' || /abort/i.test(err.message))) {
      return [];
    }
    throw err;
  }
}

/** Search/browse (SSR). Laravel expects app mount + GET /search with search, category, page, min_price, max_price, location, etc. */
export async function fetchSearch(params: {
  q?: string;
  category?: string;
  page?: number;
  min_price?: string;
  max_price?: string;
  location?: string;
  verified_only?: string;
  sort?: string;
  radius?: string;
  conditions?: string[];
  brands?: string[];
  min_rating?: string;
  listing_type?: string;
  vehicle_brand?: string;
  vehicle_model?: string;
  year_min?: string;
  year_max?: string;
  mileage_min?: string;
  mileage_max?: string;
  engine_cc_min?: string;
  engine_cc_max?: string;
  fuel_type?: string[];
  transmission?: string[];
}) {
  const verifiedOnly = params.verified_only === '1' ? '1' : '0';
  const sp = new URLSearchParams();
  if (params.q) sp.set('search', params.q);
  if (params.category) sp.set('category', params.category);
  if (params.page) sp.set('page', String(params.page));
  if (params.min_price) sp.set('min_price', params.min_price);
  if (params.max_price) sp.set('max_price', params.max_price);
  if (params.location) sp.set('location', params.location);
  sp.set('verified_only', verifiedOnly);
  if (params.sort) sp.set('sort', params.sort);
  if (params.radius) sp.set('radius', params.radius);
  (params.conditions ?? []).forEach((c) => sp.append('conditions[]', c));
  (params.brands ?? []).forEach((b) => sp.append('brands[]', b));
  if (params.min_rating) sp.set('min_rating', params.min_rating);
  if (params.listing_type) sp.set('listing_type', params.listing_type);
  if (params.vehicle_brand) sp.set('vehicle_brand', params.vehicle_brand);
  if (params.vehicle_model) sp.set('vehicle_model', params.vehicle_model);
  if (params.year_min) sp.set('year_min', params.year_min);
  if (params.year_max) sp.set('year_max', params.year_max);
  if (params.mileage_min) sp.set('mileage_min', params.mileage_min);
  if (params.mileage_max) sp.set('mileage_max', params.mileage_max);
  if (params.engine_cc_min) sp.set('engine_cc_min', params.engine_cc_min);
  if (params.engine_cc_max) sp.set('engine_cc_max', params.engine_cc_max);
  (params.fuel_type ?? []).forEach((f) => sp.append('fuel_type[]', f));
  (params.transmission ?? []).forEach((t) => sp.append('transmission[]', t));
  const query = sp.toString();
  // Bypass Next fetch cache when filters personalize or reorder results.
  const hasDynamicFilters = Boolean(
    params.q ||
      params.location ||
      params.min_price ||
      params.max_price ||
      (params.brands?.length ?? 0) > 0 ||
      params.sort ||
      params.radius ||
      params.min_rating ||
      params.listing_type ||
      (params.fuel_type?.length ?? 0) > 0 ||
      (params.transmission?.length ?? 0) > 0 ||
      params.vehicle_brand ||
      params.vehicle_model ||
      params.year_min ||
      params.year_max ||
      params.mileage_min ||
      params.mileage_max ||
      params.engine_cc_min ||
      params.engine_cc_max ||
      (params.conditions?.length ?? 0) > 0 ||
      verifiedOnly === '1',
  );
  const fetchOpts: RequestInit = hasDynamicFilters
    ? { cache: 'no-store' }
    : { next: { revalidate: 20 } };
  const data = await fetchApi<SearchPayload>(`/search${query ? `?${query}` : ''}`, fetchOpts);
  return {
    ...data,
    products: sanitizeProductsPublic(data.products) as SearchPayload['products'],
    classifiedAds: sanitizeClassifiedAdsPublic(data.classifiedAds) as SearchPayload['classifiedAds'],
  };
}

/** Single product by slug (ISR). Expect Laravel app mount + GET /products/:slug */
export const fetchProduct = cache(async (slug: string) => {
  return fetchApi<ProductDetailPayload>(`/products/${encodeURIComponent(slug)}`, { next: { revalidate: 300 } });
});

/** Dashboard data (CSR only – call from client with auth). Expect Laravel app mount + GET /dashboard (with cookies/auth) */
export async function fetchDashboard() {
  return fetchApi<DashboardPayload>('/dashboard', { cache: 'no-store' });
}

/*
 * Blog routes live at `<mount>/api/blogs` — under /api because the bare `<mount>/blogs`
 * is Laravel's own CMS page, which returns HTML.
 *
 * Passed to fetchApi as bare paths like every other fetcher, so SSR goes to the internal
 * LARAVEL_API_URL. An absolute public URL (https://buyme.lk/srs/api/...) sent the
 * server's own request out through the WAF, which fails in production and crashed /blog.
 */

/**
 * Published blog posts, fetched fresh on every request.
 *
 * The blog endpoints wrap their payload in {success, data, count}, unlike the other
 * Laravel routes here — unwrapped so call sites look like every other fetcher.
 */
export const fetchBlogs = cache(async () => {
  // no-store, not just revalidate:0 on the route — a cached fetch would keep serving
  // stale data even under an uncached page, since the longer window wins.
  const res = await fetchApi<BlogListResponse>('/api/blogs', { cache: 'no-store' });
  return { blogs: res?.data ?? [], tags: res?.tags ?? [] };
});

/** Single published blog post by numeric id. Throws ApiError(404) when absent. */
export const fetchBlog = cache(async (id: string) => {
  const res = await fetchApi<BlogDetailResponse>(`/api/blogs/${encodeURIComponent(id)}`, {
    cache: 'no-store',
  });
  return res?.data ?? null;
});

// --- Types (align with Laravel FrontendController payloads) ---

export interface Meta {
  title: string;
  description?: string;
}

export interface Category {
  id: number;
  name: string;
  icon?: string | null;
  slug: string;
}

/** Root → … → active category for search/browse breadcrumbs (subcategory pages include parents). */
export interface CategoryBreadcrumbSegment {
  id: number;
  name: string;
  slug: string;
}

/** Product list item – Laravel returns id as string, title (not name), category as string. */
export interface ProductSummary {
  id: number | string;
  name?: string;
  title?: string;
  slug: string;
  price: number;
  image?: string | null;
  category?: Category | string | null;
  is_featured?: boolean;
  /** Laravel: all catalog products are ECOMMERCE; `ads` are classified. */
  type?: 'ECOMMERCE' | 'CLASSIFIED';
  /** True when the product is linked to an official store (badge: OFFICIAL STORE vs MARKETPLACE). */
  hasStore?: boolean;
}

export interface HomePayload {
  products: ProductSummary[];
  promotedProducts?: ProductSummary[];
  trendingProducts?: ProductSummary[];
  trendingClassifiedAds?: import('@/components/ClassifiedAdCard').ClassifiedAd[];
  classifiedAds?: unknown[];
  boostedClassifiedAds?: import('@/components/ClassifiedAdCard').ClassifiedAd[];
  categories: Category[];
  verifiedStores?: unknown[];
  meta: Meta;
}

/**
 * Public site settings returned by Laravel `GET /categories` (`settings` key).
 * Keep in sync with `FrontendApiController::categories()` in buyme-backend.
 */
export interface SitePublicSettings {
  site_name?: string | null;
  site_tagline?: string | null;
  site_email?: string | null;
  site_phone?: string | null;
  site_address?: string | null;
  logo?: string | null;
  favicon?: string | null;
}

export type FooterContactSettings = Pick<
  SitePublicSettings,
  'site_tagline' | 'site_email' | 'site_phone' | 'site_address'
>;

export interface CategoriesPayload {
  categories: Category[];
  settings?: SitePublicSettings;
  meta?: Meta;
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
}

export interface SearchPayload {
  products: ProductSummary[] | Paginated<ProductSummary>;
  classifiedAds?: import('@/components/ClassifiedAdCard').ClassifiedAd[] | Paginated<import('@/components/ClassifiedAdCard').ClassifiedAd>;
  categories?: Category[];
  /**
   * Dynamic "Type" filter options for the active category (replaces legacy New/Used/Refurbished):
   *  - root category → its direct children
   *  - leaf category → its siblings
   *  - no category   → empty / omitted
   */
  categoryTypeOptions?: Category[];
  categoryTypeLabel?: string | null;
  isVehicleCategory?: boolean;
  /** Present when `category` query scopes a category; includes parent(s) for subcategories. */
  categoryBreadcrumb?: CategoryBreadcrumbSegment[];
  filters?: Record<string, unknown> & {
    category?: string;
    /** Resolved category row id for the `category` slug (child categories may not appear in `categories`). */
    categoryId?: number | null;
    categoryFilterIds?: number[] | null;
  };
  meta?: Meta;
}

export interface ProductDetailPayload {
  id: number | string;
  name?: string;
  slug: string;
  description?: string;
  price: number;
  image?: string | null;
  category?: Category | string | null;
  hasStore?: boolean;
  store?: { id: number; name: string; slug: string } | null;
  shipping_details?: Array<{ title: string; text: string }>;
  reviews?: unknown[];
  review_stats?: { average_rating: number; total_reviews: number };
  meta?: Meta;
}

export interface DashboardPayload {
  user?: { name: string; email: string };
  stats?: Record<string, number>;
  recentOrders?: unknown[];
}

export interface BlogCategory {
  categoryid: number;
  categoryname: string;
}

export interface Blog {
  blogid: number;
  blogtitle: string;
  blogbody: string;
  /** Absolute CDN URL, or null when the post has no cover image. */
  image?: string | null;
  published_at?: string | null;
  category?: BlogCategory | null;
  /** SEO fields; null until an admin fills them or they are auto-generated. */
  slug?: string | null;
  image_alt_text?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  meta_keywords?: string | null;
  blog_tags?: string[] | null;
  /** schema.org BlogPosting markup built by the backend. */
  json_ld?: Record<string, unknown> | null;
}

export interface BlogListResponse {
  success: boolean;
  data: Blog[];
  count: number;
  /** Every distinct tag in use, for the filter chips. */
  tags?: string[];
}

export interface BlogDetailResponse {
  success: boolean;
  data: Blog;
}

/**
 * Client-only: session cookies live on the Next.js origin (auth is proxied via {@link getNextProxyBase}/auth/*).
 * Do not call Laravel directly — the browser would not send those cookies cross-origin.
 */
export async function checkAuth(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  try {
    const nx = getNextProxyBase();
    const res = await fetch(`${nx}/auth/user`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return false;
    const data = (await res.json()) as { user?: unknown };
    return Boolean(data?.user);
  } catch {
    return false;
  }
}

/** Add product to cart (requires auth); proxied through Next so session cookies apply. */
let csrfTokenCache: string | null = null;
let csrfTokenFetchedAt = 0;
const CSRF_TOKEN_TTL_MS = 5 * 60 * 1000;

async function getCachedCsrfToken(forceRefresh = false): Promise<string | null> {
  const now = Date.now();
  if (!forceRefresh && csrfTokenCache && now - csrfTokenFetchedAt < CSRF_TOKEN_TTL_MS) {
    return csrfTokenCache;
  }
  try {
    const nx = getNextProxyBase();
    const csrfRes = await fetch(`${nx}/auth/csrf`, { credentials: 'include', cache: 'no-store' });
    if (!csrfRes.ok) return csrfTokenCache;
    const csrfData = (await csrfRes.json()) as { token?: string };
    const token = csrfData?.token ?? null;
    if (token) {
      csrfTokenCache = token;
      csrfTokenFetchedAt = now;
    }
    return csrfTokenCache;
  } catch {
    return csrfTokenCache;
  }
}

export async function addToCart(productId: number | string): Promise<void> {
  let token: string | null = await getCachedCsrfToken();
  const nx = getNextProxyBase();
  const res = await fetch(`${nx}/cart/add`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { 'X-CSRF-TOKEN': token } : {}),
    },
    body: JSON.stringify({
      _token: token,
      product_id: productId,
    }),
  });
  if (!res.ok) {
    // Token can expire; refresh once and retry to avoid a visible delay on every click.
    if (res.status === 419) {
      token = await getCachedCsrfToken(true);
      const retryRes = await fetch(`${nx}/cart/add`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'X-CSRF-TOKEN': token } : {}),
        },
        body: JSON.stringify({
          _token: token,
          product_id: productId,
        }),
      });
      if (retryRes.ok) return;
    }
    const errBody = await res.json().catch(() => ({}));
    const message =
      typeof errBody === 'object' && errBody && 'message' in errBody
        ? String((errBody as { message?: string }).message)
        : `Cart error ${res.status}`;
    throw new ApiError(message, res.status);
  }
}
